import { createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';

// Lazy load all pages for better performance
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Users = lazy(() => import('./pages/Users'));
const Tenants = lazy(() => import('./pages/Tenants'));
const Settings = lazy(() => import('./pages/Settings'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Reports = lazy(() => import('./pages/Reports'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const SystemSettings = lazy(() => import('./pages/SystemSettings'));
const Profile = lazy(() => import('./pages/Profile'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const TenantProfile = lazy(() => import('./pages/TenantProfile'));
const UserPreferences = lazy(() => import('./pages/UserPreferences'));
const UserNotifications = lazy(() => import('./pages/UserNotifications'));
const TenantPreferences = lazy(() => import('./pages/TenantPreferences'));
const TenantNotifications = lazy(() => import('./pages/TenantNotifications'));
const Help = lazy(() => import('./pages/Help'));
const Billing = lazy(() => import('./pages/Billing'));

// CRM Pages
const Customers = lazy(() => import('./pages/Customers'));
const Leads = lazy(() => import('./pages/Leads'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Deals = lazy(() => import('./pages/Deals'));
const Activities = lazy(() => import('./pages/Activities'));

const NotFound = lazy(() => import('./pages/NotFound'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense>,
    errorElement: <Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>
  },
  {
    path: '/login',
    element: <Suspense fallback={<LoadingFallback />}><Login /></Suspense>
  },
  {
    path: '/dashboard',
    element: <Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense>
  },
  {
    path: '/users',
    element: <Suspense fallback={<LoadingFallback />}><Users /></Suspense>
  },
  {
    path: '/tenants',
    element: <Suspense fallback={<LoadingFallback />}><Tenants /></Suspense>
  },
  {
    path: '/settings',
    element: <Suspense fallback={<LoadingFallback />}><Settings /></Suspense>
  },
  {
    path: '/analytics',
    element: <Suspense fallback={<LoadingFallback />}><Analytics /></Suspense>
  },
  {
    path: '/reports',
    element: <Suspense fallback={<LoadingFallback />}><Reports /></Suspense>
  },
  // CRM Routes
  {
    path: '/customers',
    element: <Suspense fallback={<LoadingFallback />}><Customers /></Suspense>
  },
  {
    path: '/leads',
    element: <Suspense fallback={<LoadingFallback />}><Leads /></Suspense>
  },
  {
    path: '/contacts',
    element: <Suspense fallback={<LoadingFallback />}><Contacts /></Suspense>
  },
  {
    path: '/deals',
    element: <Suspense fallback={<LoadingFallback />}><Deals /></Suspense>
  },
  {
    path: '/activities',
    element: <Suspense fallback={<LoadingFallback />}><Activities /></Suspense>
  },
  {
    path: '/profile',
    element: <Suspense fallback={<LoadingFallback />}><Profile /></Suspense>
  },
  {
    path: '/user/:userId',
    element: <Suspense fallback={<LoadingFallback />}><UserProfile /></Suspense>
  },
  {
    path: '/user/:userId/edit',
    element: <Suspense fallback={<LoadingFallback />}><UserProfile isCurrentUser={false} /></Suspense>
  },
  {
    path: '/user/:userId/preferences',
    element: <Suspense fallback={<LoadingFallback />}><UserPreferences /></Suspense>
  },
  {
    path: '/user/:userId/notifications',
    element: <Suspense fallback={<LoadingFallback />}><UserNotifications /></Suspense>
  },
  {
    path: '/tenant/:tenantId',
    element: <Suspense fallback={<LoadingFallback />}><TenantProfile /></Suspense>
  },
  {
    path: '/tenant/:tenantId/edit',
    element: <Suspense fallback={<LoadingFallback />}><TenantProfile isEdit={true} /></Suspense>
  },
  {
    path: '/tenant/:tenantId/preferences',
    element: <Suspense fallback={<LoadingFallback />}><TenantPreferences /></Suspense>
  },
  {
    path: '/tenant/:tenantId/notifications',
    element: <Suspense fallback={<LoadingFallback />}><TenantNotifications /></Suspense>
  },
  {
    path: '/help',
    element: <Suspense fallback={<LoadingFallback />}><Help /></Suspense>
  },
  {
    path: '/admin/users',
    element: <Suspense fallback={<LoadingFallback />}><UserManagement /></Suspense>
  },
  {
    path: '/admin/roles',
    element: <Suspense fallback={<LoadingFallback />}><RoleManagement /></Suspense>
  },
  {
    path: '/admin/audit-logs',
    element: <Suspense fallback={<LoadingFallback />}><AuditLogs /></Suspense>
  },
  {
    path: '/admin/system-settings',
    element: <Suspense fallback={<LoadingFallback />}><SystemSettings /></Suspense>
  },
  {
    path: '/billing',
    element: <Suspense fallback={<LoadingFallback />}><Billing /></Suspense>
  },
  {
    path: '/404',
    element: <Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>
  },
  {
    path: '*',
    element: <Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>
  }
]);

export default router; 